import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AI } from "@/constants/testIds";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Summarize this week's performance across studio + café.",
  "Which menu items should I feature next?",
  "Draft a launch caption for our new pour-over.",
  "What's the fastest way to boost client retention?",
];

export default function AIAssistantPage() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome. I'm Kairosa AI — ask me anything about your business, or click a prompt below." },
  ]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Kairosa AI is temporarily unavailable.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-4xl mx-auto flex flex-col min-h-[calc(100vh-3.5rem)]">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Business AI · Claude Sonnet 4.5
        </div>
        <h1 className="font-display text-4xl">Ask Kairosa.</h1>
        <p className="text-muted-foreground text-sm mt-1">
          I have live context on your workspace — clients, workouts, menu and orders.
        </p>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto space-y-4 py-4">
        {messages.map((m, i) => (
          <div key={i} data-testid={AI.message(i)} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[80%] rounded-2xl rounded-br-md bg-primary text-primary-foreground px-4 py-3 text-sm"
                  : "max-w-[80%] rounded-2xl rounded-bl-md bg-muted text-foreground px-4 py-3 text-sm whitespace-pre-wrap"
              }
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-3">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 pb-3">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="text-xs rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="pt-3 border-t border-border flex gap-2"
      >
        <Input
          data-testid={AI.input}
          placeholder="Ask about revenue, marketing, menu…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          className="flex-1"
        />
        <Button data-testid={AI.send} type="submit" disabled={sending}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
